import { useId, useRef, useState } from 'react';
import { ArdoqId, APIWorkspaceAttributes } from '@ardoq/api-types';
import { FlexBox, Stack } from '@ardoq/layout';
import { Tag, StatusType } from '@ardoq/status-ui';
import { Header2, Text } from '@ardoq/typography';
import { Icon, IconName, IconSize } from '@ardoq/icons';
import { IconButton } from '@ardoq/button';
import { ControlledPopover } from '@ardoq/popovers';
import { colors } from '@ardoq/design-tokens';
import { Locale } from '@ardoq/locale';
import {
  LargeImagePreviewModal,
  useMarkdownImagePreview,
  ZoomableMarkdownViewer,
  type MarkdownImagePreviewViewerProps,
} from '@ardoq/image-preview';
import { type I18nLocale, t } from '@ardoq/i18n';
import { WorkspacesDatasourceInformationFooter } from './WorkspacesDatasourceInformationFooter';
import type { GetWorkspaceManagerLinkProps } from './types';
import { getImagePreviewLabels } from './imagePreviewLabels';

export type MultipleWorkspacesDatasourceInformationProps = {
  workspaces: APIWorkspaceAttributes[];
  canManageWorkspace: (workspaceId: ArdoqId) => boolean;
  getWorkspaceManagerLinkProps: GetWorkspaceManagerLinkProps;
  locale: Locale;
  i18nLocale: I18nLocale;
};

type WorkspaceChipProps = {
  workspace: APIWorkspaceAttributes;
  canManageWorkspace: (workspaceId: ArdoqId) => boolean;
  getWorkspaceManagerLinkProps: GetWorkspaceManagerLinkProps;
  locale: Locale;
  i18nLocale: I18nLocale;
};

const WorkspaceInfoPopoverContent = ({
  workspace,
  canManageWorkspace,
  getWorkspaceManagerLinkProps,
  locale,
  i18nLocale,
  viewerProps,
}: WorkspaceChipProps & {
  viewerProps: MarkdownImagePreviewViewerProps;
}) => (
  <Stack gap="small">
    <FlexBox gap="small" align="center">
      <Icon
        iconName={IconName.WORKSPACE}
        iconSize={IconSize.MEDIUM}
        color={colors.icon}
      />
      <Header2>{workspace.name}</Header2>
      <Tag
        statusType={StatusType.DEFAULT}
        label={t(
          i18nLocale,
          'inventory:datasourceInformation.workspaceTag',
          'Workspace'
        )}
      />
    </FlexBox>
    {workspace.description?.trim() && (
      <ZoomableMarkdownViewer
        content={workspace.description}
        sanitationLevel="permissive"
        style={{ maxHeight: '250px', overflowY: 'auto' }}
        {...viewerProps}
      />
    )}
    <WorkspacesDatasourceInformationFooter
      workspace={workspace}
      canManageWorkspace={canManageWorkspace}
      getWorkspaceManagerLinkProps={getWorkspaceManagerLinkProps}
      locale={locale}
      i18nLocale={i18nLocale}
    />
  </Stack>
);

const WorkspaceChip = ({
  workspace,
  canManageWorkspace,
  getWorkspaceManagerLinkProps,
  locale,
  i18nLocale,
}: WorkspaceChipProps) => {
  const [isInfoPopoverOpen, setIsInfoPopoverOpen] = useState(false);
  const infoPopoverId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { closeButtonText, zoomButtonLabel } =
    getImagePreviewLabels(i18nLocale);
  const { viewerProps, previewImageUrl, closePreview } =
    useMarkdownImagePreview(zoomButtonLabel);

  return (
    <FlexBox gap="xsmall" align="center">
      <Icon
        iconName={IconName.WORKSPACE}
        iconSize={IconSize.MEDIUM}
        color={colors.icon}
      />
      <Text variant="text1Bold">{workspace.name}</Text>
      <ControlledPopover
        id={infoPopoverId}
        isOpen={isInfoPopoverOpen}
        onClose={() => {
          setIsInfoPopoverOpen(false);
          triggerRef.current?.focus();
        }}
        onClickOutside={() => setIsInfoPopoverOpen(false)}
        content={
          <WorkspaceInfoPopoverContent
            workspace={workspace}
            canManageWorkspace={canManageWorkspace}
            getWorkspaceManagerLinkProps={getWorkspaceManagerLinkProps}
            locale={locale}
            i18nLocale={i18nLocale}
            viewerProps={viewerProps}
          />
        }
      >
        <IconButton
          ref={triggerRef}
          iconName={IconName.INFO}
          isActive={isInfoPopoverOpen}
          onClick={() => setIsInfoPopoverOpen(prev => !prev)}
          label={t(
            i18nLocale,
            'inventory:datasourceInformation.showWorkspaceInfo',
            { name: workspace.name },
            'Show info for {{name}}'
          )}
        />
      </ControlledPopover>
      {/* Outside the popover: the modal portals to the body, so a click in it
          reads as a click outside the popover and would unmount the modal. */}
      <LargeImagePreviewModal
        imageUrl={previewImageUrl}
        onClose={closePreview}
        closeButtonText={closeButtonText}
      />
    </FlexBox>
  );
};

export const MultipleWorkspacesDatasourceInformation = ({
  workspaces,
  canManageWorkspace,
  getWorkspaceManagerLinkProps,
  locale,
  i18nLocale,
}: MultipleWorkspacesDatasourceInformationProps) => (
  <FlexBox
    gap="medium"
    align="center"
    flexWrap
    overflowY="auto"
    padding="xsmall"
    style={{ maxHeight: '200px' }}
  >
    {workspaces.map(workspace => (
      <WorkspaceChip
        key={workspace._id}
        workspace={workspace}
        canManageWorkspace={canManageWorkspace}
        getWorkspaceManagerLinkProps={getWorkspaceManagerLinkProps}
        locale={locale}
        i18nLocale={i18nLocale}
      />
    ))}
  </FlexBox>
);
